import { BsEnvelope, BsTelephone, BsTrash, BsBoxArrowUpRight, BsPencil } from 'react-icons/bs';

import YesNoFilterControl from '#globalComponents/Table/filters/YesNoFilterControl';
import { CELL_TEXT_MUTED, ROW_ACTION_BUTTON } from '#globalComponents/Table/tableStyles';
import { getGermanLabel } from '#lib/constants/contactRoles';
import { truncateName } from '#lib/textUtils';

const MISSING_BUTTON_CLASS =
	'text-sm text-warning-500 hover:text-warning-700 font-medium cursor-pointer transition-colors';

function contactInitials(contact) {
	const first = contact?.firstName?.[0] || '';
	const last = contact?.lastName?.[0] || '';
	if (first || last) return (first + last).toUpperCase();
	return (contact?.fullName?.[0] || '?').toUpperCase();
}

function hasAddress(company) {
	return Boolean(company?.addressStreet && company?.addressPostal && company?.addressCity);
}

/**
 * Column config of the contact-list contacts table.
 *
 * @param {Object} params
 * @param {Object} params.filters - Active tab filters (hasEmail, hasAddress)
 * @param {Function} params.onFilterChange - Receives a partial filter object
 * @param {boolean} params.hasCrm - Shows the "Im CRM öffnen" action
 * @param {(row: Object, tab: 'contact'|'company') => void} params.onEdit
 * @param {(companyId: string) => void} params.onOpenCompany
 * @param {(row: Object) => void} params.onRemove
 * @returns {Array<Object>}
 */
export function buildContactColumns({ filters, onFilterChange, hasCrm, onEdit, onOpenCompany, onRemove }) {
	return [
		{
			key: 'contact',
			label: 'Kontakt',
			minWidth: 160,
			render: (row) => (
				<div className="flex items-center gap-3">
					<div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
						{contactInitials(row.crmContacts)}
					</div>
					<div className="min-w-0">
						<div className="flex items-center gap-1.5">
							<span
								className="font-semibold text-text truncate"
								title={row.crmContacts.fullName || undefined}
							>
								{row.crmContacts.fullName || '-'}
							</span>
							<span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-600 flex-shrink-0">
								Kontakt
							</span>
						</div>
						{row.crmContacts.companyRole && (
							<div className={`truncate ${CELL_TEXT_MUTED}`}>
								{getGermanLabel(row.crmContacts.companyRole)}
							</div>
						)}
					</div>
				</div>
			),
		},
		{
			key: 'company',
			label: 'Unternehmen',
			minWidth: 140,
			render: (row) => {
				const companyName = row.crmContacts.crmCompanies?.companyName;
				return companyName ? (
					<span className="text-text truncate" title={companyName}>
						{truncateName(companyName)}
					</span>
				) : (
					<span className="text-bgBase-800">-</span>
				);
			},
		},
		{
			key: 'email',
			label: 'E-Mail',
			minWidth: 160,
			headerAccessory: (
				<YesNoFilterControl
					heading="E-Mail vorhanden"
					paramKey="hasEmail"
					value={filters.hasEmail}
					onChange={onFilterChange}
				/>
			),
			render: (row) =>
				row.crmContacts.emailAddress ? (
					<span className="text-bgBase-800 flex items-center gap-1 truncate max-w-[200px]">
						<BsEnvelope className="w-2.5 h-2.5 flex-shrink-0 text-primary-400" />
						{row.crmContacts.emailAddress}
					</span>
				) : (
					<button type="button" onClick={() => onEdit(row, 'contact')} className={MISSING_BUTTON_CLASS}>
						Fehlt
					</button>
				),
		},
		{
			key: 'address',
			label: 'Adresse',
			minWidth: 140,
			headerAccessory: (
				<YesNoFilterControl
					heading="Adresse vorhanden"
					paramKey="hasAddress"
					value={filters.hasAddress}
					onChange={onFilterChange}
				/>
			),
			render: (row) => {
				const company = row.crmContacts.crmCompanies;
				return hasAddress(company) ? (
					<div className="text-bgBase-800 leading-relaxed">
						<div className="truncate max-w-[180px]">{company.addressStreet}</div>
						<div className={CELL_TEXT_MUTED}>
							{company.addressPostal} {company.addressCity}
						</div>
					</div>
				) : (
					<button type="button" onClick={() => onEdit(row, 'company')} className={MISSING_BUTTON_CLASS}>
						Fehlt
					</button>
				);
			},
		},
		{
			key: 'phone',
			label: 'Telefon',
			minWidth: 110,
			render: (row) =>
				row.crmContacts.phoneNumber ? (
					<span className="text-bgBase-800 flex items-center gap-1">
						<BsTelephone className="w-2.5 h-2.5 flex-shrink-0 text-bgBase-800" />
						{row.crmContacts.phoneNumber}
					</span>
				) : (
					<span className="text-bgBase-800">-</span>
				),
		},
		{
			key: 'actions',
			label: '',
			align: 'right',
			sticky: 'right',
			width: 112,
			render: (row) => {
				const companyId = row.crmContacts.crmCompanies?.id;
				return (
					<div className="flex items-center justify-end gap-1">
						<button
							type="button"
							onClick={() => onEdit(row, 'contact')}
							className={ROW_ACTION_BUTTON}
							title="Bearbeiten"
						>
							<BsPencil />
						</button>
						{hasCrm && companyId && (
							<button
								onClick={() => onOpenCompany(companyId)}
								className={ROW_ACTION_BUTTON}
								title="Im CRM öffnen"
							>
								<BsBoxArrowUpRight />
							</button>
						)}
						<button onClick={() => onRemove(row)} className={ROW_ACTION_BUTTON} title="Aus Liste entfernen">
							<BsTrash />
						</button>
					</div>
				);
			},
		},
	];
}
